import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';

const AccountantSubeKamwatap = () => {
  const [loading, setLoading] = useState(true);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  
  // Storage for the raw fetches
  const [generateWorkData, setGenerateWorkData] = useState({});
  const [engineersData, setEngineersData] = useState({});
  
  // Final aggregated state
  const [tableData, setTableData] = useState([]);
  const [totals, setTotals] = useState({
    totalWorks: 0,
    totalSubeShown: 0,
    totalAmount: '0.00',
    challanPaidSube: 0,
    remainingWorks: 0,
    remainingAmount: '0.00',
    plantTotalWorks: 0,
    plantRemainingWorks: 0,
    plantTotalAmount: '0.00'
  });

  // Fetch Raw Data
  useEffect(() => {
    setLoading(true);

    let fetchedGenerateWork = {};
    let fetchedEngineers = {};
    let datesSet = new Set();
    
    let checks = 0;
    const processInitialData = () => {
        checks++;
        if (checks === 2) { // both listeners fired
            setGenerateWorkData(fetchedGenerateWork);
            setEngineersData(fetchedEngineers);
            
            // Extract all lotteries from generatework node keys
            Object.keys(fetchedGenerateWork).forEach(date => {
                datesSet.add(date);
            });

            const sortedDates = Array.from(datesSet).sort((a, b) => {
                const [dayA, monthA, yearA] = a.split('-');
                const [dayB, monthB, yearB] = b.split('-');
                return new Date(yearB, monthB - 1, dayB) - new Date(yearA, monthA - 1, dayA);
            });

            setAvailableDates(sortedDates);
            if (sortedDates.length > 0) {
                setSelectedDate(sortedDates[0]);
            }
            setLoading(false);
        }
    };

    const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
    const unsubWorks = onValue(generateWorkRef, (snapshot) => {
        if(snapshot.exists()) fetchedGenerateWork = snapshot.val();
        else fetchedGenerateWork = {};
        processInitialData();
    });

    const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
    const unsubEngs = onValue(engRef, (snapshot) => {
        if(snapshot.exists()) fetchedEngineers = snapshot.val();
        else fetchedEngineers = {};
        processInitialData();
    });

    return () => {
        unsubWorks();
        unsubEngs();
    };
  }, []);

  // Recalculate Aggregations based on selected date
  useEffect(() => {
    if(!selectedDate || loading) return;

    // 1. Flatten "Sube" Works from specific date in generatework
    let flattenedWorks = [];
    const worksForDate = generateWorkData[selectedDate] || {};
    
    if (typeof worksForDate === 'object') {
        Object.values(worksForDate).forEach((value) => {
            if (value && typeof value === 'object') {
                if (value.sharedwork === 'सुबे') {
                    flattenedWorks.push({
                        workNumber: value.workNumber || '-',
                        taluka: value.taluka ? value.taluka.trim() : 'Unknown',
                        tenderAmount: parseFloat(value.tenderAmount || 0)
                    });
                }
            }
        });
    }

    // 2. Base Taluka Map Layout
    const talukaMap = {};
    const predefinedTalukas = ['ठाणे', 'उल्हासनगर', 'कल्याण', 'भिवंडी', 'मुरबाड', 'अंबरनाथ', 'शहापूर'];
    predefinedTalukas.forEach(taluka => {
        talukaMap[taluka] = {
            taluka: taluka,
            totalWorks: 0,
            totalAmount: 0,
            totalSubeShown: 0,
            challanPaidSube: 0,
            remainingWorks: 0,
            remainingAmount: 0,
            plantTotalWorks: 0,
            plantRemainingWorks: 0,
            plantTotalAmount: 0
        };
    });

    // Populate Work Counts
    flattenedWorks.forEach(work => {
        const t = work.taluka;
        if (!talukaMap[t]) {
            talukaMap[t] = { taluka: t, totalWorks: 0, totalAmount: 0, totalSubeShown: 0, challanPaidSube: 0, remainingWorks: 0, remainingAmount: 0, plantTotalWorks: 0, plantRemainingWorks: 0, plantTotalAmount: 0 };
        }
        talukaMap[t].totalWorks += 1;
        talukaMap[t].totalAmount += work.tenderAmount;
        talukaMap[t].remainingWorks += 1;
        talukaMap[t].remainingAmount += work.tenderAmount;
    });

    // 3. Process Engineer Registrations for Expressions of Interest & Challan & Lottery Allocations
    Object.values(engineersData).forEach(user => {
        // Track Interest & Challans
        if (user.work_payment) {
            Object.values(user.work_payment).forEach(payment => {
                if (payment.selectedWorks && Array.isArray(payment.selectedWorks)) {
                    // Check if challan paid
                    const isPaid = payment.status === 'approved' || payment.feeStatus === 'success';

                    payment.selectedWorks.forEach(workStr => {
                        const cleanWorkStr = String(workStr).trim();
                        const workNum = cleanWorkStr.split(' - ')[0].trim();
                        const match = flattenedWorks.find(w => String(w.workNumber).trim() === workNum);
                        
                        if (match && talukaMap[match.taluka]) {
                            talukaMap[match.taluka].totalSubeShown += 1;
                            if (isPaid) {
                                talukaMap[match.taluka].challanPaidSube += 1;
                            }
                        }
                    });
                }
            });
        }

        // Note: User explicitly requested: "कामवाटपामधील उर्वरित कामे me wo एकूण कामे fetch wo wo yaha pr bhi fetch karna he"
        // Meaning Remaining Works should EXACTLY MATCH Total Works for this view.
        // Therefore, we skip deducting allocation totals here based on GeneratedLottery at this specific request.
    });

    // Build Final Array and Calculate Totals
    let buildTableData = [];
    let calcTotals = {
        totalWorks: 0,
        totalSubeShown: 0,
        totalAmount: 0,
        challanPaidSube: 0,
        remainingWorks: 0,
        remainingAmount: 0,
        plantTotalWorks: 0,
        plantRemainingWorks: 0,
        plantTotalAmount: 0
    };

    let idCounter = 1;
    // We push predefined ones first to maintain order, then any unknown talukas
    predefinedTalukas.forEach(t => {
        buildTableData.push({ id: idCounter++, ...talukaMap[t] });
        delete talukaMap[t];
    });
    Object.keys(talukaMap).forEach(key => {
        buildTableData.push({ id: idCounter++, ...talukaMap[key] });
    });

    buildTableData.forEach(row => {
        calcTotals.totalWorks += row.totalWorks;
        calcTotals.totalAmount += row.totalAmount;
        calcTotals.totalSubeShown += row.totalSubeShown;
        calcTotals.challanPaidSube += row.challanPaidSube;
        calcTotals.remainingWorks += row.remainingWorks;
        calcTotals.remainingAmount += row.remainingAmount;
    });

    // Format currency to Indian Rupees notation visually without the symbol
    const formatCurrency = (val) => {
        return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    let filteredTableData = buildTableData.filter(row => row.totalWorks > 0);

    setTableData(filteredTableData.map(row => ({
        ...row,
        totalAmount: formatCurrency(row.totalAmount),
        remainingAmount: formatCurrency(row.remainingAmount),
        plantTotalAmount: formatCurrency(row.plantTotalAmount)
    })));

    setTotals({
        ...calcTotals,
        totalAmount: formatCurrency(calcTotals.totalAmount),
        remainingAmount: formatCurrency(calcTotals.remainingAmount),
        plantTotalAmount: formatCurrency(calcTotals.plantTotalAmount)
    });

  }, [selectedDate, generateWorkData, engineersData]);


  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[95%] mx-auto bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        {/* Header Section */}
        <div className="bg-slate-800 text-white shadow-md p-6 text-center">
          <h1 className="text-3xl font-bold">सु.बे. यादी (तालुकानिहाय अहवाल)</h1>
        </div>

        {/* Search Header */}
        <div className="p-6 border-b border-gray-100 bg-white">
          <div className="flex flex-col gap-2">
            <label className="text-gray-700 font-medium">
              लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-4">
              <div className="relative inline-block w-64">
                <select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded leading-tight focus:outline-none focus:border-blue-500 appearance-none transition-colors"
                >
                  {availableDates.length > 0 ? (
                      availableDates.map(date => (
                        <option key={date} value={date}>{date}</option>
                      ))
                  ) : (
                      <option value="">तारीख उपलब्ध नाही</option>
                  )}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
            {loading && <p className="text-sm text-blue-500 animate-pulse mt-2">माहिती लोड होत आहे...</p>}
          </div>
        </div>

        {/* Table Content */}
        {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-[10px] font-semibold border-b border-gray-200 uppercase tracking-widest text-center">
                <th className="px-2 py-4 border-r border-gray-200">अ.क्र.</th>
                <th className="px-2 py-4 border-r border-gray-200 text-left">तालुका</th>
                <th className="px-2 py-4 border-r border-gray-200">एकूण कामे</th>
                <th className="px-2 py-4 border-r border-gray-200">इच्छापत्रात दर्शविलेले एकूण सुबे</th>
                <th className="px-2 py-4 border-r border-gray-200 text-right">एकूण रक्कम</th>
                <th className="px-2 py-4 border-r border-gray-200">कामाचे चलन भरलेले एकूण सुबे</th>
                <th className="px-2 py-4 border-r border-gray-200">कामवाटपामधील उर्वरित कामे</th>
                <th className="px-2 py-4 border-r border-gray-200 text-right">उर्वरित कामाची एकूण रक्कम</th>
                <th className="px-2 py-4 border-r border-gray-200">प्लॅन्ट मधील एकूण कामे</th>
                <th className="px-2 py-4 border-r border-gray-200">प्लॅन्ट कामवाटपामधील शिल्लक कामे</th>
                <th className="px-2 py-4 text-right">एकूण रक्कम</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 text-xs">
              {tableData.length > 0 ? tableData.map((row) => (
                <tr key={row.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                  <td className="px-2 py-4 text-center border-r border-gray-100 font-medium">{row.id}</td>
                  <td className="px-2 py-4 text-left border-r border-gray-100 font-bold whitespace-nowrap">{row.taluka}</td>
                  <td className="px-2 py-4 text-center border-r border-gray-100">{row.totalWorks}</td>
                  <td className="px-2 py-4 text-center border-r border-gray-100">{row.totalSubeShown}</td>
                  <td className="px-2 py-4 text-right border-r border-gray-100 font-bold tracking-wide">{row.totalAmount}</td>
                  <td className="px-2 py-4 text-center border-r border-gray-100">{row.challanPaidSube}</td>
                  <td className="px-2 py-4 text-center border-r border-gray-100 font-bold">{row.remainingWorks}</td>
                  <td className="px-2 py-4 text-right border-r border-gray-100 font-bold text-red-600 tracking-wide">{row.remainingAmount}</td>
                  <td className="px-2 py-4 text-center border-r border-gray-100">{row.plantTotalWorks}</td>
                  <td className="px-2 py-4 text-center border-r border-gray-100">{row.plantRemainingWorks}</td>
                  <td className="px-2 py-4 text-right font-bold tracking-wide">{row.plantTotalAmount}</td>
                </tr>
              )) : (
                  <tr><td colSpan="11" className="text-center py-6 text-gray-500">कोणतीही माहिती उपलब्ध नाही.</td></tr>
              )}
              {/* Footer Row */}
              {tableData.length > 0 && (
              <tr className="bg-slate-100 font-black text-slate-800 text-xs uppercase tracking-widest border-t-2 border-slate-300">
                <td className="px-4 py-4 text-right border-r border-gray-300" colSpan={2}>एकूण Total:</td>
                <td className="px-2 py-4 text-center border-r border-gray-300">{totals.totalWorks}</td>
                <td className="px-2 py-4 text-center border-r border-gray-300">{totals.totalSubeShown}</td>
                <td className="px-2 py-4 text-right border-r border-gray-300 text-blue-700">{totals.totalAmount}</td>
                <td className="px-2 py-4 text-center border-r border-gray-300">{totals.challanPaidSube}</td>
                <td className="px-2 py-4 text-center border-r border-gray-300">{totals.remainingWorks}</td>
                <td className="px-2 py-4 text-right border-r border-gray-300 text-red-700">{totals.remainingAmount}</td>
                <td className="px-2 py-4 text-center border-r border-gray-300">{totals.plantTotalWorks}</td>
                <td className="px-2 py-4 text-center border-r border-gray-300">{totals.plantRemainingWorks}</td>
                <td className="px-2 py-4 text-right text-gray-600">{totals.plantTotalAmount}</td>
              </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
};

export default AccountantSubeKamwatap;
